import axios from 'axios';

/**
* Client for the Shopify API
*/
class API {
  /**
  * Fetch cart data
  * @returns {object} Cart data
  */
  async getCart() {
    try {
      const { data } = await axios({
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        },
        url: `${routes.cart_url}.js`,
      });
      return data;
    } catch(error) {
      console.error(`Error: ${error.message}`);
      return error.response;
    } 
  }

  /**
  * Add products to cart
  * @param {{
  *   items: object,
  *   sections: string,
  * }} config  – Contains the products to add,
  * the quantity and section to update
  * @returns {object} Line items associated with the added items and sections
  */
  async addToCart({ items, sections = undefined }) {
    let formData = {
        items: items
      };

    // Support bundled section rendering
    if (sections) {
      formData.sections = sections;
    }

    try {
      const { data } = await axios({
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        url: `${routes.cart_add_url}.js`,
        data: JSON.stringify(formData),
      });
      return data;
    } catch (error) {
      console.error(`Error: ${error.message}`);
      return error.response;
    }
  }

  /**
  * Update the cart's line item quantities
  * @param {{
  *   id: number,
  *   quantity: number,
  *   sections: string
  * }} config – Contains the product variant,
  * the quantity and section to update
  * @returns {object} The JSON of the cart and HTML of the sections
  */
  async updateCart({
    id,
    quantity,
    sections = undefined,
  }) {

    let formData = {
      updates: {
        [id]: quantity,
      }
    };

    //Support bundled section rendering
    if (sections) {
      formData.sections = sections;
    }

    try {
      const { data } = await axios({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        url: `${routes.cart_update_url}.js`,
        data: JSON.stringify(formData),
      });
      return data;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  /**
   * It empties the cart
   */
  async emptyCart() {
    try {
      const { data } = await axios({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        url: `${routes.cart_clear_url}.js`
      });
      return data;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * Render up to five sections with the use of Section Rendering API
  * @param {string} sections – section IDs
  * @returns {object} Includes pairs for each section ID and its
  * corresponding rendered HTML
  */
  async renderShopifySection(sections) {
    try {
      const {
        data: html
      } = await axios.get(`?sections=${sections}`);
      return html;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  async shopifySectionByUrl(base, section) {
    try {
      const {
        data: html
      } = await axios.get(`${base}?section_id=${section}`);
      return html;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  async shopifySectionByUrlAndParams(base, params) {
    const paramsArray = Object.entries(params)
    const paramsConcatenated = '?' + paramsArray
      .map(item => item.join('='))
      .join('&')

    try {
      const {
        data: html
      } = await axios.get(`${base}${paramsConcatenated}`);
      return html;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * It is used to recommend related products for a specific product
  * @param {{
  *   id: number,
  *   limit: number,
  *   sectionId: string
  * }} config – Product ID on which the recommendation will be based,
  * the result limit and the section with which the recommended
  * products will be rendered
  * @returns {object} Array with the recommended products or, if the section ID
  * is supplied, HTML from a section rendered with product recommendations
  */
  async getRecommendedProducts({ id, limit, sectionId = undefined }) {
    try {
      let url;
      if (sectionId) {
        //Support section rendering
        url = `/recommendations/products?section_id=${sectionId}&product_id=${id}&limit=${limit}`;
      } else {
        url = `/recommendations/products.json?product_id=${id}&limit=${limit}`;
      }
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * Get data from a resource
  * @param {string} url – The path of the resource to obtain
  * @returns {object} Data from a resource
  */
  async read(url) {
    try {
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * Get promocode data from a codes service
  * @param {string} code – The code for which data is fetched
  * @returns {object} Data from a resource
  * */
  async getGiftCodeData(code) {
    try {
      const { data } = await axios({
        method: "GET",
        headers: {
          'Content-Type': 'application/json'
        },
        url: `${routes.service_url}?code=${encodeURIComponent(code)}&shop=${window.Shopify.shop}`,
      });
  
      return data;
    } catch(error) {
      console.error(`Error: ${error.message}`);
    }
    
  }

  /**
  * Apply discount code
  * @param {string} discountCode – The code to apply
  * @param {string} sectionIdArray – The array of section ids to return
  * @param {string} signal – signal
  * @returns {object} updated data or empty object
  * */
  async applyDiscount (discountCode, sectionIdArray = [], signal) {
    try {
      const discountResponse = await fetch(`${window.Shopify.routes.root}discount/${discountCode}`, { signal });
      if (!discountResponse.ok) {
        console.warn("Failed to apply discount:", discountResponse.statusText);
        return;
      } else {
        console.log("Discount applied successfully.");
      }
      const updateResponse = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sections: sectionIdArray,
        }),
        signal,
      });
  
      const updateData = await updateResponse.json();
  
      if (updateResponse.ok) {
        console.log("Updated cart sections:", updateData.sections);
        return updateData;
      } else {
        console.error("Failed to update cart:", updateResponse.statusText);
        throw new Error(updateData.message || "An error occurred while updating the cart.");
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error("Error applying discount or updating cart:", error.message);
      throw error;
    }
  }
}

export default new API();
